import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileUp, Loader2, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";

interface DocumentUploadProps {
  leadId: string;
  itineraryItemId?: string;
  onUploadComplete?: () => void;
}

export const DocumentUpload = ({ leadId, itineraryItemId, onUploadComplete }: DocumentUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/vnd.apple.numbers': ['.numbers'],
    },
    multiple: false,
    maxSize: 20 * 1024 * 1024, // 20MB
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file selected");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Insert document record first, then let the backend read the stored file
      const { data: inserted, error: insertError } = await supabase
        .from('documents')
        .insert({
          lead_id: leadId,
          itinerary_item_id: itineraryItemId,
          user_id: user.id,
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          notes: notes,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // AI scan (server-side; reads the actual stored file)
      let scanNote: string | null = null;
      try {
        const { data: analysis, error: analysisError } = await supabase.functions.invoke(
          'analyze-document',
          { body: { documentId: inserted.id } }
        );
        if (analysisError) {
          console.error('Analysis error:', analysisError);
          scanNote = "The document was saved, but the AI scan failed — you can fill in the details manually.";
        } else if (analysis?.unsupported) {
          scanNote = analysis.reason as string;
        }
      } catch (err) {
        console.error('Analysis failed:', err);
        scanNote = "The document was saved, but the AI scan failed — you can fill in the details manually.";
      }

      return { scanNote };
    },
    onSuccess: ({ scanNote }) => {
      toast({
        title: scanNote ? "Document uploaded" : "Document uploaded",
        description: scanNote || "Document has been uploaded and analyzed successfully",
        variant: scanNote ? "destructive" : undefined,
      });
      setFile(null);
      setNotes("");
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      onUploadComplete?.();
    },

    onError: (error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <Label>Upload Document</Label>
        <div
          {...getRootProps()}
          className={`mt-2 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          }`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2">
            {isDragActive ? (
              <>
                <Upload className="h-12 w-12 text-primary animate-bounce" />
                <p className="text-primary font-medium">Drop file here...</p>
              </>
            ) : (
              <>
                <FileUp className="h-12 w-12 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Drag and drop a file here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  PDFs, Word docs, text files, images (JPG, PNG, WEBP), spreadsheets (Excel, CSV, Numbers)
                </p>
                <p className="text-xs text-muted-foreground">Max file size: 20MB</p>
              </>
            )}
          </div>
        </div>
        {file && (
          <div className="mt-2 p-3 bg-muted rounded-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileUp className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFile(null)}
              disabled={uploadMutation.isPending}
            >
              Remove
            </Button>
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="document-notes">Notes (Optional)</Label>
        <Textarea
          id="document-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes about this document..."
          disabled={uploadMutation.isPending}
          rows={3}
        />
      </div>

      <Button
        onClick={() => uploadMutation.mutate()}
        disabled={!file || uploadMutation.isPending}
        className="w-full"
      >
        {uploadMutation.isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <FileUp className="mr-2 h-4 w-4" />
            Upload Document
          </>
        )}
      </Button>
    </div>
  );
};
